import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, finalize, of, startWith, switchMap } from 'rxjs';
import { BoardNovaPoshtaService } from '../../../services/board-nova-poshta.service';
import { formatMoneyAmount } from '../../board-v3/price-field.utils';
import { NpCabinet, NpDocument, NpShipment } from '../nova-poshta.types';
import { formatNpPhone, formatTtn, isTtnFormat, NP_SHIPMENT_STATUS_STYLES } from '../nova-poshta.utils';

/**
 * Sidebar that links an existing Nova Poshta waybill to a deal: one of the recent waybills of a cabinet, found by
 * the number, the recipient name or phone, or any waybill by its number. Closes with the linked waybill.
 */
@Component({
  selector: 'app-nova-poshta-link-form',
  templateUrl: './nova-poshta-link-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full' },
})
export class NovaPoshtaLinkFormComponent implements OnInit {
  @Input({ required: true }) cardId!: string;

  @Output() close = new EventEmitter<NpShipment | void>();

  private readonly npService = inject(BoardNovaPoshtaService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly cabinetControl = new FormControl<string | null>(null);
  protected readonly searchControl = new FormControl('', { nonNullable: true });

  protected cabinets: NpCabinet[] = [];
  protected documents: NpDocument[] = [];
  /** The waybill found by its number when the recent ones do not have it */
  protected found: NpDocument | null = null;
  protected selected: NpDocument | null = null;
  protected loading = true;
  protected searching = false;
  protected finding = false;
  protected notFound = false;
  protected saving = false;

  protected readonly formatTtn = formatTtn;
  protected readonly formatNpPhone = formatNpPhone;
  protected readonly formatMoneyAmount = formatMoneyAmount;

  /** A full number the recent waybills do not have: it can be looked up in Nova Poshta */
  protected get lookupNumber(): string | null {
    const number = this.searchControl.value.replace(/\s/g, '');
    if (!isTtnFormat(number) || this.documents.some((document) => document.ttn_number === number)) return null;
    return this.found?.ttn_number === number ? null : number;
  }

  ngOnInit(): void {
    combineLatest([
      this.cabinetControl.valueChanges.pipe(startWith(this.cabinetControl.value), distinctUntilChanged()),
      this.searchControl.valueChanges.pipe(startWith(''), debounceTime(300), distinctUntilChanged()),
    ])
      .pipe(
        switchMap(([cabinetId, search]) => {
          this.found = null;
          this.notFound = false;
          if (!cabinetId) return of([]);
          this.searching = true;
          return this.npService.getCabinetDocuments(cabinetId, search).pipe(
            // A failed search keeps the next ones going
            catchError((error) => {
              this.npService.error(error);
              return of([]);
            }),
            finalize(() => (this.searching = false)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((documents) => {
        this.documents = documents;
        if (this.selected && !this.isListed(this.selected)) this.selected = null;
      });

    this.npService
      .getCabinets()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (cabinets) => {
          this.cabinets = cabinets;
          this.cabinetControl.setValue((cabinets.find((cabinet) => cabinet.is_default) ?? cabinets[0])?.id ?? null);
        },
        error: (error) => this.npService.error(error),
      });
  }

  protected statusBadge(document: NpDocument): string {
    return NP_SHIPMENT_STATUS_STYLES[document.status].badge;
  }

  protected isSelectable(document: NpDocument): boolean {
    return !document.linked_card;
  }

  protected select(document: NpDocument): void {
    if (this.isSelectable(document)) this.selected = document;
  }

  protected find(): void {
    const number = this.lookupNumber;
    const cabinetId = this.cabinetControl.value;
    if (!number || !cabinetId || this.finding) return;

    this.finding = true;
    this.notFound = false;
    this.npService
      .findDocument(cabinetId, number)
      .pipe(finalize(() => (this.finding = false)))
      .subscribe({
        next: (document) => {
          this.found = document;
          this.select(document);
        },
        error: (error) => {
          if (this.npService.getErrorCode(error) === 'NP_SHIPMENT_NOT_FOUND') this.notFound = true;
          else this.npService.error(error);
        },
      });
  }

  protected save(): void {
    const document = this.selected;
    const cabinetId = this.cabinetControl.value;
    if (!document || !cabinetId || this.saving) return;

    this.saving = true;
    this.npService
      .linkShipment(this.cardId, { cabinet_id: cabinetId, ttn_number: document.ttn_number })
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (shipment) => {
          this.npService.success('novaposhta.link.linked', { ttn: formatTtn(shipment.ttn_number) });
          this.close.emit(shipment);
        },
        error: (error) => this.npService.error(error),
      });
  }

  private isListed(document: NpDocument): boolean {
    return this.found?.ttn_number === document.ttn_number || this.documents.some((item) => item.ttn_number === document.ttn_number);
  }
}
